import { ParentSummary } from '@features/parent-page/components/ParentSummary'
import { useCreateParentAccountMutation, useParentSuspense, useResetParentPasswordMutation } from '@features/parent-page/queries'
import { Button, Group, Paper, PasswordInput, Radio, Stack, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { CopyTextButton } from '@ui/CopyTextButton'
import { hasPermission } from '@features/rbac/permissions'

export const Route = createFileRoute('/_layout/parents/$parentId/')({
    component: RouteComponent,
})

function RouteComponent() {

    const { parentId } = Route.useParams()
    const [createdPassword, setCreatedPassword] = useState<string | null>(null)

    const { data: parent } = useParentSuspense(parentId)

    return (
        <Stack gap={7}>
            {hasPermission(['parents.manage']) && <Group gap={7}>
                {!parent.hasRegisteredAccount && <CreateAccountButton parentId={parentId} onCreated={setCreatedPassword} />}
                {createdPassword && <Group gap={7}><Text size="sm">Temporary password:</Text><Group wrap="nowrap" gap={3}><Paper px={5} withBorder radius="sm"><Text size="sm">{createdPassword}</Text></Paper><CopyTextButton text={createdPassword} /></Group></Group>}
                {parent.hasRegisteredAccount && <Button
                    onClick={() => openResetPasswordModal(parentId)}
                    disabled={!parent.oid}
                    title={!parent.oid ? "Password can only be reset after first log in" : undefined}
                >
                    Reset password
                </Button>}
            </Group>}
            <ParentSummary parentId={parentId}/>
        </Stack>
    )
}

function CreateAccountButton({ parentId, onCreated }: { parentId: string; onCreated: (password: string) => void }) {
    const mutation = useCreateParentAccountMutation(parentId)

    return <>
        <Button loading={mutation.isPending} onClick={() => modals.openConfirmModal({
            title: 'Create parent account',
            children: <Text size="sm">Create an external account for this parent? A temporary password will be generated and must be changed at first sign-in.</Text>,
            labels: { confirm: 'Create account', cancel: 'Cancel' },
            onConfirm: () => mutation.mutate(undefined, { onSuccess: (data) => onCreated(data.password) }),
        })}>Create account</Button>
        {mutation.error && <Text c="red" size="sm">Account creation failed. Please try again.</Text>}
    </>
}

function openResetPasswordModal(parentId: string) {
    modals.open({ title: 'Reset parent password', children: <ResetPasswordForm parentId={parentId} /> })
}

function ResetPasswordForm({ parentId }: { parentId: string }) {
    const mutation = useResetParentPasswordMutation(parentId)
    const [mode, setMode] = useState<'generate' | 'custom'>('generate')
    const [password, setPassword] = useState('')
    const [result, setResult] = useState<string | null>(null)

    return (
        <Stack>
            <Radio.Group value={mode} onChange={(value) => setMode(value as typeof mode)}>
                <Stack gap="xs">
                    <Radio value="generate" label="Let Microsoft generate a password" />
                    <Radio value="custom" label="Set a custom password" />
                </Stack>
            </Radio.Group>
            {mode === 'custom' && <PasswordInput label="New password" value={password} onChange={(event) => setPassword(event.currentTarget.value)} />}
            {result && <Group gap={7}><Text size="sm">Temporary password: </Text><Group wrap="nowrap" gap={3}><Paper px={5} withBorder radius="sm"><Text size="sm">{result}</Text></Paper><CopyTextButton text={result}/></Group></Group>}
            {mutation.error && <Text c="red" size="sm">Password reset failed. Please try again.</Text>}
            <Text size="sm" c="dimmed">The parent will be required to choose a new password when they log in.</Text>
            <Button loading={mutation.isPending} disabled={(mode === 'custom' && password.length < 8) || mutation.isSuccess} onClick={() => mutation.mutate(mode === 'custom' ? password : null, { onSuccess: (data) => setResult(data.password) })}>
                Reset password
            </Button>
        </Stack>
    )
}
